import { apiJSON, authFetch } from './auth/token';

export type SkillField = { name: string; type: string };

export type SkillDef = {
  id: string;
  name: string;
  category: string;
  icon?: string;
  description: string;
  body?: string;
  version?: string;
  latency?: string;
  color?: string;
  inputs?: SkillField[];
  outputs?: SkillField[];
  matched_path?: string;
  author?: string;
  github_repo?: string;
  date?: string;
  path_in_repo?: string;
  tags?: string[];
  source?: string;
  runtime?: string;
  concepts?: string[];
  discipline?: string;
  inactive?: boolean;
  active?: boolean;
};

export type PipelineNode = {
  id: string;
  skill_id: string;
  x: number;
  y: number;
  note?: string;
  body?: string;
};

export type PipelineEdge = { id: string; from: string; to: string; field: string };
export type GroupType = 'process' | 'loop';
export type CanvasGroup = { id: string; type: GroupType; x: number; y: number; w: number; h: number; title: string; iterations?: number };
export type StickyNote = { id: string; x: number; y: number; w: number; h: number; text: string };
export type PollAction = { kind?: string; url?: string; query?: string; urls?: string[] };
export type Scheduler = {
  id: string;
  x: number;
  y: number;
  title?: string;
  enabled: boolean;
  cadence?: string;
  timezone?: string;
  action?: PollAction;
  target_ids?: string[];
  last_fired_at?: string;
};
export type PipelineStep = { id: string; skill_id: string; inputs: { source: string; field: string }[] };
export type PlaybookRecord = {
  id: string;
  owner_id: string;
  outcome_id: string;
  run_id?: string;
  kind: string;
  dedupe_key: string;
  payload?: Record<string, unknown>;
  created_at?: string;
};

export type Outcome = {
  id: string;
  owner_id?: string;
  project_id?: string;
  title: string;
  slug?: string;
  description?: string;
  trigger?: string;
  success_criteria?: string[];
  discipline?: string;
  is_public?: boolean;
  price_cents?: number;
  downloads?: number;
  author?: string;
  author_initials?: string;
  nodes: PipelineNode[];
  edges: PipelineEdge[];
  groups: CanvasGroup[];
  stickies: StickyNote[];
  schedulers?: Scheduler[];
  pipeline_steps: PipelineStep[];
  composition?: {
    nodes?: PipelineNode[];
    edges?: PipelineEdge[];
    pipeline_steps?: PipelineStep[];
  };
  playbook?: {
    overview?: string;
    rules?: string[];
    heuristics?: string[];
    failure_modes?: string[];
  };
  contract?: {
    inputs?: Array<{ name: string; type: string; description?: string }>;
    outputs?: Array<{ name: string; type: string; description?: string }>;
  };
  score?: { overall?: number; checks?: Array<{ criterion_id: string; passed: boolean; score: number; reason: string }> };
  test_rig?: { epochs?: number; run_id?: string; model?: string; latency_ms?: number; certified_at?: string; fixture_names?: string[] };
  quality_score?: number;
  last_run?: unknown;
  user_score?: number;
  llm_score?: number;
  best_user_score?: number;
  best_llm_score?: number;
  run_lock?: { run_id: string; started_at: string };
};

export type RunLog = { at: string; message: string };
export type LiveStep = {
  step_id: string;
  skill_id: string;
  skill_name: string;
  status: string;
  log: string;
  duration_ms: number;
  output_path?: string;
};
export type ScoreCheck = { id: string; passed: boolean; reason: string };

export type LiveRun = {
  outcome_id: string;
  run_id: string;
  status: string;
  phase: string;
  step_index: number;
  step_total: number;
  current_skill_id?: string;
  current_skill_name?: string;
  started_at: string;
  elapsed_ms: number;
  logs: RunLog[];
  plan_md?: string;
  steps?: LiveStep[];
  commit?: string;
  commit_message?: string;
  success?: boolean;
  provider?: string;
  model?: string;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  user_score?: number;
  llm_score?: number;
  review_md?: string;
  checks?: ScoreCheck[];
  user_checks?: ScoreCheck[];
  epoch?: number;
};

export type RunStart = { outcome_id: string; run_id: string; status: string; epoch?: number };

export type ArtifactNode = {
  id: string;
  name: string;
  kind: 'file' | 'folder';
  path?: string;
  size?: string;
  lang?: string;
  content?: string;
  children?: ArtifactNode[];
};

export type RunStep = { step_id: string; skill_name: string; status: string; log: string; duration_ms: number };
export type RunTrace = { outcome_id: string; steps: RunStep[]; success: boolean; run_id?: string };

export type User = { id: string; email: string; name?: string; provider?: string; role?: string; banned?: boolean; theme?: 'light' | 'dark' };
export type Account = {
  owner_id: string;
  credit_balance: number;
  metered_units?: number;
  subscription: {
    plan?: string;
    status: string;
    current_period_end?: string;
    kind?: string;
    custom_domain?: boolean;
  };
  transactions: { id: string; type: string; amount: number; created_at: string; external_id?: string }[];
  url?: string;
};

export type DeployStatus = {
  id?: string;
  phase: string;
  hostname?: string;
  custom_domain?: string;
  name_servers?: string[];
  error?: string;
};

export type PublishResult = {
  deploy_id?: string;
  deploy?: DeployStatus;
  url?: string;
  error?: string;
};

export type CreditPack = {
  id: string;
  name: string;
  credits: number;
  price_cents: number;
  blurb?: string;
};

export type Project = {
  id: string;
  owner_id?: string;
  title: string;
  outcome_description: string;
  outcome_type: string;
  outcome_id?: string;
  created_at: string;
  updated_at: string;
};

export const VALID_OUTCOME_TYPES = ['app', 'business', 'api', 'video', 'document', 'agent', 'workflow', 'other'];

export const listSkills = () => apiJSON<SkillDef[]>('/api/skills');
export const getSkill = (id: string) => apiJSON<SkillDef>('/api/skills/' + id);
export const resolveSkill = (description: string, trigger = '') =>
  apiJSON<SkillDef>('/api/skills/resolve', { method: 'POST', body: JSON.stringify({ description, trigger }) });

export const registerUser = (email: string, password: string, name: string) =>
  apiJSON<User>('/api/auth/register', { method: 'POST', body: JSON.stringify({ email, password, name }) });
export const loginUser = (email: string, password: string) =>
  apiJSON<{ token: string; user: User }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
export const getMe = () => apiJSON<User>('/api/me');
export const patchMeTheme = (theme: 'light' | 'dark') =>
  apiJSON<User>('/api/me', { method: 'PATCH', body: JSON.stringify({ theme }) });

export const createOutcome = (o: Partial<Outcome>) =>
  apiJSON<Outcome>('/api/outcomes', { method: 'POST', body: JSON.stringify(o) });
export const updateOutcome = (id: string, o: Partial<Outcome>) =>
  apiJSON<Outcome>(`/api/outcomes/${id}`, { method: 'PUT', body: JSON.stringify(o) });
export const listOutcomes = () => apiJSON<Outcome[]>('/api/outcomes');
export const getOutcome = (id: string) => apiJSON<Outcome>(`/api/outcomes/${id}`);
export const updateOutcomeListing = (id: string, body: { is_public?: boolean; price_cents?: number }) =>
  apiJSON<Outcome>(`/api/outcomes/${id}/listing`, { method: 'PATCH', body: JSON.stringify(body) });
export const listOutcomeRecords = (id: string) => apiJSON<PlaybookRecord[]>(`/api/outcomes/${id}/records`);
export const upsertOutcomeRecord = (id: string, rec: Partial<PlaybookRecord>) =>
  apiJSON<PlaybookRecord>(`/api/outcomes/${id}/records`, { method: 'POST', body: JSON.stringify(rec) });
export const runOutcome = (id: string) =>
  apiJSON<RunStart>(`/api/outcomes/${id}/run`, { method: 'POST' });
export const getRunStatus = (outcomeId: string, runId: string) =>
  apiJSON<LiveRun>(`/api/outcomes/${outcomeId}/runs/${runId}`);
export const getArtifacts = (id: string) => apiJSON<ArtifactNode[]>(`/api/outcomes/${id}/artifacts`);
export async function downloadOutcomeExport(id: string): Promise<void> {
  const res = await authFetch(`/api/outcomes/${id}/export`);
  if (!res.ok) {
    let msg = `export failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'skillset.zip';
  a.click();
  URL.revokeObjectURL(url);
}
export const scoreRun = (outcomeId: string, runId: string, score: number, note = '', checks: ScoreCheck[] = []) =>
  apiJSON<{ user_score: number; llm_score: number; review_md: string; checks?: ScoreCheck[]; user_checks?: ScoreCheck[] }>(
    `/api/outcomes/${outcomeId}/runs/${runId}/score`,
    { method: 'POST', body: JSON.stringify({ score, note, checks }) },
  );
export const reviewRun = (outcomeId: string, runId: string, note: string) =>
  apiJSON<{ user_score: number; llm_score: number; review_md: string; checks?: ScoreCheck[]; user_checks?: ScoreCheck[] }>(
    `/api/outcomes/${outcomeId}/runs/${runId}/review`,
    { method: 'POST', body: JSON.stringify({ note }) },
  );
export const trainOutcome = (id: string, fromRunId: string) =>
  apiJSON<RunStart>(`/api/outcomes/${id}/train`, { method: 'POST', body: JSON.stringify({ from_run_id: fromRunId }) });
export const getTrace = (id: string) => apiJSON<RunTrace>(`/api/outcomes/${id}/trace`);

export const buyCredits = (pack: string) =>
  apiJSON<Account>('/api/billing/credits', { method: 'POST', body: JSON.stringify({ pack }) });
export const listCreditPacks = () => apiJSON<{ packs: CreditPack[]; stripe: boolean }>('/api/billing/packs');
export const subscribe = (plan: string) =>
  apiJSON<Account>('/api/billing/subscribe', { method: 'POST', body: JSON.stringify({ plan }) });
export const buyCustomDomain = (domain: string, outcomeId?: string) =>
  apiJSON<Account>('/api/billing/domain', { method: 'POST', body: JSON.stringify({ domain, outcome_id: outcomeId }) });
export const cancelSubscription = () =>
  apiJSON<Account>('/api/billing/cancel', { method: 'POST' });
export const getAccount = () => apiJSON<Account>('/api/account');

export const publishOutcome = async (id: string, body: { custom_domain?: string } = {}): Promise<PublishResult> => {
  const res = await authFetch(`/api/outcomes/${id}/publish`, { method: 'POST', body: JSON.stringify(body) });
  const data = (await res.json().catch(() => ({}))) as PublishResult;
  if (res.status === 402) return data;
  if (!res.ok) throw new Error(data.error || `request failed (${res.status})`);
  return data;
};
export const getDeploy = (id: string) => apiJSON<DeployStatus>(`/api/outcomes/${id}/deploy`);

export type UsageModelRow = {
  model: string;
  provider: string;
  tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
};

export type UsageKindRow = {
  kind: string;
  label: string;
  tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
};

export type UsageProjectRow = {
  project_id: string;
  project_title: string;
  tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  by_model: UsageModelRow[];
  by_kind: UsageKindRow[];
};

export type UsageMonth = {
  month: string;
  key: string;
  tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
};

export type UsageReport = {
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  this_month_tokens: number;
  months: UsageMonth[];
  by_model: UsageModelRow[];
  by_project: UsageProjectRow[];
  by_kind: UsageKindRow[];
};

export const getUsage = () => apiJSON<UsageReport>('/api/usage');

export const listProjects = () => apiJSON<Project[]>('/api/projects');
export const createProject = (p: Partial<Project> & { template?: string }) =>
  apiJSON<Project>('/api/projects', { method: 'POST', body: JSON.stringify(p) });
export const getProject = (id: string) => apiJSON<Project>(`/api/projects/${id}`);
export const patchProject = (id: string, p: Partial<Project>) =>
  apiJSON<Project>(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify(p) });
export const deleteProject = (id: string) =>
  authFetch(`/api/projects/${id}`, { method: 'DELETE' });

export type ProjectFile = {
  id: string;
  name: string;
  size_bytes: number;
  created_at?: string;
  extracted_text?: string;
};

export const PROJECT_FILE_ACCEPT =
  '.pdf,.txt,.md,.markdown,.csv,.json,.jsonl,.yml,.yaml,.xml,.html,.css,.py,.ts,.tsx,.js,.jsx,.go,.rs,.java,.rb,.c,.h,.cpp,.cs,.swift,.kt,.sql,.sh,.toml';

export const listProjectFiles = (projectId: string) =>
  apiJSON<ProjectFile[]>(`/api/projects/${projectId}/files`);
export const getProjectFile = (projectId: string, fileId: string) =>
  apiJSON<ProjectFile>(`/api/projects/${projectId}/files/${fileId}`);
export const deleteProjectFile = (projectId: string, fileId: string) =>
  authFetch(`/api/projects/${projectId}/files/${fileId}`, { method: 'DELETE' });
export const uploadProjectFile = (projectId: string, file: File) => {
  const body = new FormData();
  body.append('file', file);
  return apiJSON<ProjectFile>(`/api/projects/${projectId}/files`, { method: 'POST', body });
};

export type AdminProject = {
  name: string;
  type: string;
  score: number;
  tokens: number;
  models?: { model: string; provider: string; tokens: number }[];
};
export type AdminUser = {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'member';
  credits: number;
  tokens: number;
  models?: { model: string; provider: string; tokens: number }[];
  banned: boolean;
  joined_at: string;
  last_active: string;
  projects: AdminProject[];
};

export const listAdminUsers = () => apiJSON<AdminUser[]>('/api/admin/users');
export const patchAdminUser = (id: string, body: { banned?: boolean; credits?: number }) =>
  apiJSON<AdminUser>(`/api/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
export const impersonateAdminUser = (id: string) =>
  apiJSON<{ token: string; user: User }>(`/api/admin/users/${id}/impersonate`, { method: 'POST' });

export type AdminSkill = SkillDef & { active: boolean; inactive?: boolean };

export type AdminSkillList = {
  total: number;
  active: number;
  inactive: number;
  empty_io: number;
  limit: number;
  offset: number;
  skills: AdminSkill[];
  disciplines?: { name: string; count: number; with_io?: number }[];
  categories?: { name: string; count: number; discipline?: string }[];
  io_types?: { type: string; inputs: number; outputs: number }[];
};

export type AdminSkillQuery = {
  q?: string;
  mode?: 'keyword' | 'graph';
  active?: 'all' | 'true' | 'false';
  produces?: string;
  consumes?: string;
  concepts?: string;
  around_id?: string;
  discipline?: string;
  category?: string;
  limit?: number;
  offset?: number;
};

export const listAdminSkills = (query: AdminSkillQuery = {}) => {
  const q = new URLSearchParams();
  if (query.q) q.set('q', query.q);
  if (query.mode) q.set('mode', query.mode);
  if (query.active) q.set('active', query.active);
  if (query.produces) q.set('produces', query.produces);
  if (query.consumes) q.set('consumes', query.consumes);
  if (query.concepts) q.set('concepts', query.concepts);
  if (query.around_id) q.set('around_id', query.around_id);
  if (query.discipline) q.set('discipline', query.discipline);
  if (query.category) q.set('category', query.category);
  if (query.limit != null) q.set('limit', String(query.limit));
  if (query.offset != null) q.set('offset', String(query.offset));
  const qs = q.toString();
  return apiJSON<AdminSkillList>(`/api/admin/skills${qs ? `?${qs}` : ''}`);
};

export const patchAdminSkill = (
  id: string,
  body: Partial<Pick<SkillDef, 'name' | 'description' | 'body' | 'category' | 'icon' | 'inputs' | 'outputs' | 'concepts' | 'tags'>> & {
    active?: boolean;
  },
) => apiJSON<AdminSkill>(`/api/admin/skills/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

export const deleteAdminSkill = (id: string) =>
  apiJSON<void>(`/api/admin/skills/${id}`, { method: 'DELETE' });

export type LLMProvider = 'lmstudio' | 'openai' | 'anthropic' | 'openrouter';

export type LLMSettings = {
  provider: LLMProvider;
  model: string;
  base_url: string;
  openai_key_set: boolean;
  anthropic_key_set: boolean;
  openrouter_key_set: boolean;
};

export type ChatTurn = { role: 'user' | 'assistant'; content: string };

export type ChatRunCheck = { id: string; passed: boolean; reason: string };

export type ChatRunSnapshot = {
  run_id: string;
  model?: string;
  prompt_tokens?: number | null;
  duration_ms?: number | null;
  user_score?: number | null;
  llm_score?: number | null;
  review_md?: string;
  checks?: ChatRunCheck[];
};

export type ChatPlaybook = {
  skill_id: string;
  bullets: { content: string; helpful: number; harmful: number }[];
};

export type ChatProject = {
  id?: string;
  title: string;
  description: string;
  outcome_type?: string;
  nodes: { id: string; skill_id: string; note?: string; body?: string }[];
  edges: { from: string; to: string; field: string }[];
  files?: { name: string; content: string }[];
  runs?: ChatRunSnapshot[];
  playbooks?: ChatPlaybook[];
};

export type ChatRecommendation = { id: string; name: string; why: string; kind?: 'playbook' | 'skill' };

export type ChatReply = { content: string; created_skills: SkillDef[]; recommendations?: ChatRecommendation[] };

export const getLLMSettings = () => apiJSON<LLMSettings>('/api/llm/settings');
export const putLLMSettings = (s: Partial<LLMSettings> & { openai_key?: string; anthropic_key?: string }) =>
  apiJSON<LLMSettings>('/api/llm/settings', { method: 'PUT', body: JSON.stringify(s) });
export const listLLMModels = (opts?: { baseUrl?: string; provider?: LLMProvider }) => {
  const q = new URLSearchParams();
  if (opts?.baseUrl) q.set('base_url', opts.baseUrl);
  if (opts?.provider) q.set('provider', opts.provider);
  const qs = q.toString();
  return apiJSON<{ models: string[]; loaded?: string[] }>(`/api/llm/models${qs ? `?${qs}` : ''}`);
};
export const sendChat = (body: {
  provider?: LLMProvider;
  model?: string;
  base_url?: string;
  openai_key?: string;
  anthropic_key?: string;
  context?: string;
  mode?: 'analyze' | 'compose' | 'match' | 'landing';
  messages: ChatTurn[];
  project: ChatProject;
}) => apiJSON<ChatReply>('/api/chat', { method: 'POST', body: JSON.stringify(body) });
export const createSkill = (s: Partial<SkillDef>) =>
  apiJSON<SkillDef>('/api/skills', { method: 'POST', body: JSON.stringify(s) });

export const forkOutcome = (outcomeId: string, title?: string) =>
  apiJSON<{ project_id: string; id: string }>(`/api/outcomes/${encodeURIComponent(outcomeId)}/fork`, {
    method: 'POST',
    body: JSON.stringify({ title }),
  });

